/** Indent / outdent keyboard actions for task lists. */
import { safeGenerateKeyBetween } from '@/features/tasks/utils/interactions/listKeyHelpers';

const getSelectedTask = (state) => {
  if (state.selectedIndex > -1) {
    return state.filteredTasks[state.selectedIndex];
  }
  return null;
};

const findParent = (state, parentId) => {
  return state.allTasks.find((t) => t.id === parentId);
};

const calculatePositionForRoot = (parent, allTasks) => {
  const parentSiblings = allTasks.filter(
    (t) => (t.parentId ?? null) === (parent.parentId ?? null),
  );
  const currentIndex = parentSiblings.findIndex((t) => t.id === parent.id);

  const previousKey =
    currentIndex >= 0 ? parentSiblings[currentIndex].orderKey : null;
  const nextKey =
    currentIndex < parentSiblings.length - 1
      ? parentSiblings[currentIndex + 1].orderKey
      : null;

  return { previousKey, nextKey };
};

const moveTaskOutdent = (task, newParentId, positionData, state) => {
  const moveFn = state.moveTask || state.updateTask;
  moveFn(
    task.id,
    {
      parentId: newParentId,
      orderKey: safeGenerateKeyBetween(
        positionData.previousKey,
        positionData.nextKey,
      ),
    },
    state.userId,
  );
};

const getIndentOrderKey = (siblings) => {
  if (siblings.length === 0) {
    return safeGenerateKeyBetween(null, null);
  }
  const sortedSiblings = [...siblings].sort((a, b) =>
    (a.orderKey || '') < (b.orderKey || '')
      ? -1
      : (a.orderKey || '') > (b.orderKey || '')
        ? 1
        : 0,
  );
  const lastChild = sortedSiblings[sortedSiblings.length - 1];
  return safeGenerateKeyBetween(lastChild.orderKey, null);
};

export const handleOutdent = (state) => {
  const task = getSelectedTask(state);
  if (!task || !task.parentId) return;

  const currentParent = findParent(state, task.parentId);
  if (!currentParent) return;

  const newParentId = currentParent.parentId ?? null;
  if (newParentId === null && state.allowOutdentToRoot === false) return;

  const positionData = calculatePositionForRoot(currentParent, state.allTasks);
  moveTaskOutdent(task, newParentId, positionData, state);
};

export const handleIndent = (state) => {
  const task = state.filteredTasks[state.selectedIndex];
  if (state.allowIndentToRoot === false && task && !task.parentId) return;

  if (state.selectedIndex > 0 && task) {
    const prevTask = state.filteredTasks[state.selectedIndex - 1];
    const newParentId =
      !task.parentId && prevTask.parentId ? prevTask.parentId : prevTask.id;

    const siblings = state.allTasks.filter((t) => t.parentId === newParentId);
    const newOrderKey = getIndentOrderKey(siblings);

    const moveFn = state.moveTask || state.updateTask;
    moveFn(
      task.id,
      { parentId: newParentId, orderKey: newOrderKey },
      state.userId,
    );

    if (prevTask.date || prevTask.time) {
      const updateFn = state.updateTask || state.moveTask;
      updateFn(prevTask.id, { date: null, time: null }, state.userId);
    }
  }
};
